// packages/web/src/components/admin/user-edit-sheet.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  useUpdateUser,
  useSetRole,
  useLinkErp,
  useBan,
  useUnban,
  useResendInvite,
} from "@/api/users/mutations";
import { ApproverPicker, type ApproverCandidate } from "./approver-picker.tsx";
import { InviteLinkDialog } from "./invite-link-dialog.tsx";
import { planUserSave, type Role, type BanReason } from "@/lib/plan-user-save";
import type { UserAdmin } from "@/api/generated/types.gen";

export interface UserEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserAdmin;
  approverCandidates: ApproverCandidate[];
}

export function UserEditSheet({
  open,
  onOpenChange,
  user,
  approverCandidates,
}: UserEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [defaultApproverId, setDefaultApproverId] = useState<string | null>(user.defaultApproverId);
  const [erpEmployeeNumber, setErp] = useState<string>(user.erpEmployeeNumber ?? "");
  const [isActive, setIsActive] = useState(user.isActive);
  const [roleChangeReason, setRoleChangeReason] = useState("");
  const [banReason, setBanReason] = useState<BanReason>("DEACTIVATED");

  const updateMut = useUpdateUser();
  const setRoleMut = useSetRole();
  const linkErpMut = useLinkErp();
  const banMut = useBan();
  const unbanMut = useUnban();
  const resendMut = useResendInvite();

  const [inviteDialog, setInviteDialog] = useState<null | {
    email: string;
    url: string;
    emailDeliveryFailed: boolean;
  }>(null);

  async function onResendInvite() {
    try {
      const result = await resendMut.mutateAsync({ id: user.id });
      setInviteDialog({
        email: user.email,
        url: result.invite.url,
        emailDeliveryFailed: result.invite.emailDeliveryFailed,
      });
    } catch {
      toast.error(t("admin.users.edit-sheet.toast.resend-failed"));
    }
  }

  const isWorker = role === "worker";
  const roleChanged = role !== user.role;
  const reasonInvalid = roleChanged && roleChangeReason.trim().length < 3;
  const showBanReason = user.isActive && !isActive;

  async function onSave() {
    if (reasonInvalid) return;
    const steps = planUserSave(
      {
        name: user.name,
        role: user.role,
        defaultApproverId: user.defaultApproverId,
        erpEmployeeNumber: user.erpEmployeeNumber,
        isActive: user.isActive,
      },
      {
        name,
        role,
        defaultApproverId,
        erpEmployeeNumber: erpEmployeeNumber || null,
        isActive,
        roleChangeReason,
        banReason,
      },
    );
    try {
      for (const step of steps) {
        if (step.op === "update") await updateMut.mutateAsync({ id: user.id, body: step.body });
        else if (step.op === "setRole")
          await setRoleMut.mutateAsync({ id: user.id, body: step.body });
        else if (step.op === "linkErp")
          await linkErpMut.mutateAsync({ id: user.id, body: step.body });
        else if (step.op === "ban") await banMut.mutateAsync({ id: user.id, body: step.body });
        else if (step.op === "unban") await unbanMut.mutateAsync({ id: user.id });
      }
      toast.success(t("admin.users.edit-sheet.toast.saved"));
      onOpenChange(false);
    } catch (e) {
      const message =
        typeof e === "object" && e && "message" in e
          ? String((e as { message: unknown }).message)
          : t("admin.users.edit-sheet.toast.unknown-error");
      toast.error(t("admin.users.edit-sheet.toast.failed", { message }));
    }
  }

  const pending =
    updateMut.isPending ||
    setRoleMut.isPending ||
    linkErpMut.isPending ||
    banMut.isPending ||
    unbanMut.isPending;

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction="right">
        <DrawerContent className="ml-auto h-full w-full max-w-md bg-surface">
          <DrawerHeader>
            <DrawerTitle>{user.name}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-4">
            {user.inviteStatus === "pending" || user.inviteStatus === "expired" ? (
              <div className="rounded-3 border border-line-strong bg-surface-1 p-3 text-[12.5px] text-ink-700">
                {user.inviteStatus === "expired"
                  ? t("admin.users.edit-sheet.invite-banner.expired")
                  : t("admin.users.edit-sheet.invite-banner.pending")}
                <Button variant="ghost" className="ml-2" onClick={onResendInvite}>
                  {t("admin.users.edit-sheet.invite-banner.resend.label")}
                </Button>
              </div>
            ) : null}
            {user.roleOverrideActive ? (
              <div className="rounded-3 border border-line-strong bg-surface-1 p-3 text-[12.5px] text-ink-700">
                {t("admin.users.edit-sheet.entra-override", {
                  role: user.roleEntra ?? t("admin.users.edit-sheet.entra-override-unknown"),
                })}
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="ues-name">{t("admin.users.edit-sheet.name.label")}</Label>
              <Input id="ues-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ues-role">{t("admin.users.edit-sheet.role.label")}</Label>
              <select
                id="ues-role"
                aria-label={t("admin.users.edit-sheet.role.label")}
                className="h-9 w-full rounded-3 border border-line bg-surface px-2 text-[13px]"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                <option value="worker">{t("common.roles.worker.label")}</option>
                <option value="approver">{t("common.roles.approver.label")}</option>
                <option value="super_admin">{t("common.roles.super-admin.label")}</option>
              </select>
              {roleChanged ? (
                <div className="mt-2 space-y-1.5">
                  <Label htmlFor="ues-reason">
                    {t("admin.users.edit-sheet.role-change.label")}
                  </Label>
                  <Input
                    id="ues-reason"
                    value={roleChangeReason}
                    onChange={(e) => setRoleChangeReason(e.target.value)}
                    aria-invalid={reasonInvalid}
                  />
                  {reasonInvalid ? (
                    <p className="mt-1 text-[12px] text-vroom-red">
                      {t("admin.users.edit-sheet.role-change.missing")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {isWorker ? (
              <div className="space-y-1.5">
                <Label>{t("admin.users.edit-sheet.default-approver.label")}</Label>
                <ApproverPicker
                  value={defaultApproverId}
                  onChange={setDefaultApproverId}
                  candidates={approverCandidates}
                />
                {!defaultApproverId ? (
                  <p className="mt-1 text-[12px] text-vroom-red">
                    {t("admin.users.edit-sheet.default-approver.missing")}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="ues-erp">{t("admin.users.edit-sheet.erp.label")}</Label>
              <Input
                id="ues-erp"
                value={erpEmployeeNumber}
                onChange={(e) => setErp(e.target.value)}
                placeholder={t("admin.users.edit-sheet.erp.placeholder")}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="ues-active">{t("admin.users.edit-sheet.active.label")}</Label>
              <Switch id="ues-active" checked={isActive} onCheckedChange={setIsActive} />
            </div>
            {showBanReason ? (
              <div className="space-y-1.5">
                <Label htmlFor="ues-ban-reason">
                  {t("admin.users.edit-sheet.ban-reason.label")}
                </Label>
                <select
                  id="ues-ban-reason"
                  className="h-9 w-full rounded-3 border border-line bg-surface px-2 text-[13px]"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value as BanReason)}
                >
                  <option value="DEACTIVATED">
                    {t("admin.users.edit-sheet.ban-reason.deactivated")}
                  </option>
                  <option value="ARCHIVED">
                    {t("admin.users.edit-sheet.ban-reason.archived")}
                  </option>
                </select>
              </div>
            ) : null}
          </div>
          <DrawerFooter className="flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("common.buttons.cancel.label")}
            </Button>
            <Button variant="primary" onClick={onSave} disabled={pending}>
              {t("common.buttons.save.label")}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {inviteDialog ? (
        <InviteLinkDialog
          open
          onOpenChange={(v) => !v && setInviteDialog(null)}
          email={inviteDialog.email}
          url={inviteDialog.url}
          emailDeliveryFailed={inviteDialog.emailDeliveryFailed}
        />
      ) : null}
    </>
  );
}
